import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Receipt, ChevronLeft, ChevronRight, ShoppingCart, Crown } from 'lucide-react';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import type { BillingInvoice, InvoiceKind } from '@blogforge/shared';
import { formatCredits, formatDate, formatMoney } from '../lib/billing-format.js';

// ─────────────────────────────────────────────────────────────────
// Invoices — both kinds of invoice records for the active workspace:
// 'credits' (one-time credit top-ups) and 'subscription' (recurring
// plan charges, once those exist). Filterable by kind.
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const KIND_FILTERS: Array<{ id: 'all' | InvoiceKind; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'credits', label: 'Credit top-ups' },
  { id: 'subscription', label: 'Subscription' },
];

const KIND_META: Record<InvoiceKind, { label: string; icon: typeof Crown; chip: string }> = {
  credits: {
    label: 'Credits',
    icon: ShoppingCart,
    chip: 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]',
  },
  subscription: {
    label: 'Subscription',
    icon: Crown,
    chip: 'bg-ink/5 text-ink-soft',
  },
};

const STATUS_TONE: Record<string, string> = {
  paid: 'bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]',
  open: 'bg-ink/5 text-ink-soft',
  draft: 'bg-ink/5 text-muted',
  void: 'bg-ink/5 text-muted',
  uncollectible: 'bg-danger/10 text-danger',
};

export function InvoicesPage() {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const [page, setPage] = useState(1);
  const [kindFilter, setKindFilter] = useState<'all' | InvoiceKind>('all');

  useEffect(() => {
    setPage(1);
    setKindFilter('all');
  }, [activeWorkspaceId]);

  const { data, isLoading } = useQuery({
    queryKey: ['billing', 'invoices', activeWorkspaceId, page, kindFilter],
    queryFn: () =>
      api.billing.invoices({
        page,
        limit: PAGE_SIZE,
        ...(kindFilter === 'all' ? {} : { kind: kindFilter }),
      }),
    enabled: Boolean(activeWorkspaceId),
  });

  const invoices = data?.data ?? [];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[15px] font-bold tracking-tight text-ink">
          Invoice records
          {typeof data?.total === 'number' && (
            <span className="ml-2 text-[12px] font-medium text-muted">
              {data.total.toLocaleString()} total
            </span>
          )}
        </h2>

        <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-line bg-bg/60 p-1">
          {KIND_FILTERS.map((k) => (
            <button
              key={k.id}
              type="button"
              onClick={() => {
                setKindFilter(k.id);
                setPage(1);
              }}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-all duration-150 ${
                kindFilter === k.id ? 'bg-card text-ink shadow-sm' : 'text-muted hover:text-ink-soft'
              }`}
            >
              {k.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted">
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
          Loading invoices…
        </div>
      ) : invoices.length === 0 ? (
        <div className="surface-row flex h-40 flex-col items-center justify-center text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/10 text-accent">
            <Receipt size={20} />
          </div>
          <p className="text-sm font-semibold text-ink">No invoices yet</p>
          <p className="mt-1 max-w-xs text-[13px] text-muted">
            Buying credit packages (or, later, subscribing to a plan) creates invoice records here.
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {invoices.map((inv) => (
            <InvoiceRow key={inv.id} invoice={inv} />
          ))}
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-[12px] text-muted">
            Page {data.page} of {data.totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={data.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="btn-ghost px-2.5 py-1.5 text-xs"
            >
              <ChevronLeft size={13} />
              Newer
            </button>
            <button
              type="button"
              disabled={data.page >= data.totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="btn-ghost px-2.5 py-1.5 text-xs"
            >
              Older
              <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoiceRow({ invoice }: { invoice: BillingInvoice }) {
  const kind = KIND_META[invoice.kind];
  const KindIcon = kind.icon;
  const statusTone = STATUS_TONE[invoice.status] ?? 'bg-ink/5 text-muted';

  return (
    <div className="surface-row flex items-center gap-4 px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bg text-muted">
        <KindIcon size={15} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">
          {invoice.kind === 'credits'
            ? `Credit top-up — ${formatCredits(invoice.creditAmountMicro ?? 0)} credits`
            : invoice.periodDays
              ? `Subscription charge — ${invoice.periodDays} days`
              : 'Subscription charge'}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${kind.chip}`}>
            {kind.label}
          </span>
          <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${statusTone}`}>
            {invoice.status}
          </span>
          <span className="font-mono text-[11px] text-muted">{formatDate(invoice.createdAt)}</span>
          {invoice.providerRef && (
            <span className="hidden font-mono text-[10.5px] text-muted sm:inline">
              {invoice.providerRef}
            </span>
          )}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <p className="font-mono text-[13.5px] font-semibold text-ink">
          {formatMoney(invoice.amountDue, invoice.currency)}
        </p>
        <p className="font-mono text-[10.5px] text-muted">
          {invoice.status === 'paid' && invoice.paidAt
            ? `paid ${formatDate(invoice.paidAt)}`
            : invoice.status}
        </p>
      </div>
    </div>
  );
}
